// ============================================================
// shared/gamme.js — LA GAMME EST L'OBJET CENTRAL.
// ------------------------------------------------------------
// C'est le renversement du 5 août :
//
//     relevé  →  GAMME  →  temps  →  prix
//
// L'agent d'estimation ne produit plus trois nombres agrégés. Il
// produit une gamme opératoire, une ligne par opération, et **les trois
// temps en sont DÉRIVÉS**. Corriger la gamme recalcule les temps, donc
// le prix.
//
// La raison est celle de l'apprentissage : le jour où le modèle propose
// exactement la bonne gamme, il est juste. Tant qu'on lui demande trois
// nombres agrégés, on n'a **aucune prise** sur ce qui les produit — ni
// pour corriger, ni pour apprendre.
//
// ══════════════════════════════════════════════════════════════
// LE POINT OÙ « LES TROIS TEMPS SONT LES SOMMES DE LA GAMME » EST FAUX
// ══════════════════════════════════════════════════════════════
//
// Seul le CYCLE est une somme qui suit la quantité. Les deux autres ont
// une règle propre, évidente pour l'atelier — et c'est exactement pour
// ça qu'on l'oublie en codant.
//
// | catégorie | unité autorisée | × quantité ? | qui la paie |
// |---|---|---|---|
// | `cycle` | `par_piece` (ou `par_lot` pour le débit barre) | **oui** | chaque pièce |
// | `montage` | `par_lot` **uniquement** | non | la première pièce |
// | `programmation` | `par_projet` **uniquement** | non | le projet, une fois |
//
// **Le montage n'est JAMAIS multiplié par la quantité.** Dans les mots
// de l'atelier : *c'est la première pièce qui coûte.* Monter la pièce,
// prendre l'origine, sortir le premier article — on le fait une fois.
//
// **La programmation n'intervient qu'une fois par projet.** Pas par
// pièce, pas même par lot. Si le programme existe déjà — révision,
// relance, commande répétée — elle vaut zéro.
//
// Le contrôle qui doit exister : chiffrer la même pièce à 1, 10 et 50.
// `montage_lot` et `programmation_projet` doivent afficher **exactement
// les mêmes minutes** dans les trois cas. Si l'un des deux bouge avec la
// quantité, l'amortissement est appliqué deux fois — ou pas du tout.
// ============================================================

/** Les trois catégories. Miroir du CHECK en base. */
// ══════════════════════════════════════════════════════════════
// LE MODÈLE DE GAMME A CHANGÉ — RDV Charles, 11 août 2026.
// ──────────────────────────────────────────────────────────────
// **Il n'y a pas de programmation globale chez Mertens.** Pour chaque
// prise, un opérateur programme la machine, la fait tourner en
// essai-erreur, et monte la pièce. Tout ça, c'est LA PREMIÈRE PIÈCE.
// Ensuite, ça produit.
//
// Donc : **une OP = une prise**, et chaque OP porte DEUX lignes.
//
//     OP 10 — prototype     montage + programmation + première passe
//                         PAR LOT, une seule fois
//     OP 10 — production  le cycle
//                         PAR PIÈCE, à chaque pièce
//
// Idem OP 20, 30… et **la gamme se termine toujours par une OP de
// contrôle**, elle aussi en deux lignes. Une pièce à tolérances serrées
// ou un client exigeant demandent plus de temps de contrôle.
//
// ── Pourquoi les anciennes catégories RESTENT ──
//
// Un devis déjà chiffré ne se convertit pas : son prix est photographié
// et doit se rouvrir au centime dans six mois. `cycle`, `montage` et
// `programmation` restent donc valides et dérivent comme avant. Ce sont
// les gammes NEUVES qui emploient le nouveau vocabulaire.
//
// Les deux jeux coexistent sans se mélanger : la dérivation rend les
// deux représentations, et le prix additionne les mêmes quatre paniers
// dans les deux cas.
// ══════════════════════════════════════════════════════════════
export const CATEGORIES_GAMME = [
  // ── Le vocabulaire COURANT ──
  'prototype',
  'production',
  'controle',
  // ── L'ANCIEN, conservé pour les devis déjà chiffrés ──
  'cycle',
  'montage',
  'programmation',
];

/** Ce que l'agent d'estimation a le droit de produire AUJOURD'HUI. */
export const CATEGORIES_GAMME_COURANTES = ['prototype', 'production', 'controle'];

/**
 * LE NOM D'UNE CATÉGORIE, ET IL N'Y EN A QU'UN.
 *
 * Deux écrans en portaient chacun le sien : la gamme affichait
 * « prototype », l'écran Apprentissage « Mise en train (1re pièce) ».
 * Le même poste, deux mots — c'est-à-dire, pour qui lit les deux, deux
 * postes.
 *
 * ── POURQUOI « PROTOTYPE » ET PAS « 1re PIÈCE » ──
 *
 * Parce que Charles a tranché le 11 août 2026, et dans l'autre sens :
 * le mot ÉTAIT « pièce 1 » et il a été remplacé partout, base comprise.
 * « Pièce 1 » décrit un RANG, et personne ne devinait que la ligne
 * porte le montage de la machine et la programmation — ce qui coûte
 * cher en petite série. « Prototype » décrit le TRAVAIL.
 *
 * Revenir à « 1re pièce » pour faire plus simple rejouerait exactement
 * la confusion qui a motivé le renommage. Ce qui manquait n'était pas
 * un autre mot : c'était que l'explication VOYAGE avec lui. C'est le
 * rôle de `aide`, juste en dessous.
 */
// ══════════════════════════════════════════════════════════════
// « PROTOTYPE » DEVIENT « MISE EN ROUTE » — réunion du 1er sept. 2026.
// ──────────────────────────────────────────────────────────────
// Raph et Charles : le mot décrit mal le travail. Un « prototype »
// évoque une pièce d'essai qu'on jette ; ici la ligne porte le montage,
// la programmation et la PREMIÈRE PIÈCE BONNE, qui part chez le client.
//
// « Première pièce » a été écarté une seconde fois, pour la raison du
// 11 août : il décrit un RANG, pas le travail.
//
// **Le mot change à l'écran, la base ne bouge pas.** `categorie` reste
// `'prototype'` : convertir obligerait à migrer des devis déjà chiffrés
// dont le prix est photographié, pour un mot. C'est la même règle que
// `justification_reglage`, qui garde « réglage » depuis le 5 août.
// ══════════════════════════════════════════════════════════════
export const LIBELLE_CATEGORIE = {
  // ── Le vocabulaire COURANT ──
  prototype: 'Mise en route',
  production: 'Production',
  controle: 'Contrôle',
  // ── L'ANCIEN, pour les devis déjà chiffrés : ils gardent leur gamme
  //    telle qu'elle est partie chez le client. ──
  cycle: 'Cycle',
  montage: 'Montage',
  programmation: 'Programmation',
};

/** Les trois totaux affichés, dans l'ordre où on les lit. */
export const TOTAUX_GAMME = [
  { cle: 'prototype', label: 'Mise en route', aide: 'le montage, la programmation et la première pièce — une fois par lot' },
  { cle: 'production', label: 'Production', aide: 'le cycle, à chaque pièce' },
  { cle: 'controle', label: 'Contrôle', aide: 'la première pièce, puis chaque pièce produite' },
];

// ══════════════════════════════════════════════════════════════
// LA GAMME EN DEUX BLOCS — réunion Mertens du 1er septembre 2026.
// ──────────────────────────────────────────────────────────────
// Charles et Raph : *« on arrête de dédoubler chaque OP en ligne
// prototype + ligne production. À la place, deux blocs : tout ce qui est
// mise en route d'abord, puis la production à la pièce. »*
//
// Le motif est celui de toute la réunion : **le programme va trop loin
// dans le détail, il se plante sur ce détail, et ce détail n'est pas ce
// qu'on lui demande.** Une gamme qui alterne
// `10-proto, 10-produ, 20-proto, 20-produ…` se lit en zigzag, et c'est
// dans ce zigzag que les deux boutons du 27 août ont pu casser les
// paires sans que personne le voie.
//
// ── CE QUI CHANGE, ET CE QUI NE CHANGE SURTOUT PAS ──
//
// Ce qui change : l'ORDRE de lecture, à l'écran comme dans le prompt, et
// le fait qu'une OP n'est plus TENUE de porter ses deux lignes.
//
// Ce qui ne change pas : **`op_num` reste l'identité d'une PRISE**, et
// c'est lui qui décide si une production se compte `× qté` ou
// `× (qté − 1)`. C'est physique, pas cosmétique — la mise en route d'une
// prise contient le premier article de CETTE prise. Déplacer cet
// appariement vers autre chose ferait bouger des prix, et la réunion n'a
// jamais demandé ça. Le banc de gammes (`api/test/banc-gammes.test.js`)
// tient les quatre prix au centime pendant tout ce remaniement.
//
// ── OÙ VA LE CONTRÔLE ──
//
// Il ne fait pas un troisième bloc : il se range dans ses deux voisins,
// exactement comme `totauxAffichables` le fond depuis le 11 août. Le
// contrôle du premier article est de la mise en route ; celui de la
// série est de la production. L'écran et les totaux disent donc la même
// chose — sans ça, on lirait deux découpages du même travail.
// ══════════════════════════════════════════════════════════════

/** Les deux blocs, dans l'ordre où on les lit. */
export const BLOCS_GAMME = [
  {
    cle: 'mise_en_route',
    label: 'Mise en route',
    aide: 'le montage, la programmation et la première pièce — une fois pour le lot',
  },
  {
    cle: 'production',
    label: 'Production',
    aide: 'le cycle, sur chaque pièce qui reste à faire',
  },
];

/**
 * Dans quel bloc se range une ligne.
 *
 * L'ancien vocabulaire (`cycle`, `montage`, `programmation`) se range
 * aussi : un devis d'avant le 11 août doit s'ouvrir en deux blocs comme
 * les autres. Il garde son calcul, il change seulement de place à
 * l'écran.
 */
export function blocDeLaLigne(ligne) {
  switch (ligne?.categorie) {
    case 'prototype':
    case 'montage':
    case 'programmation':
      return 'mise_en_route';
    case 'controle':
      // Premier article → mise en route ; série → production.
      return ligne?.unite === 'par_piece' ? 'production' : 'mise_en_route';
    default:
      return 'production';
  }
}

/**
 * La gamme rangée en deux blocs, chacun trié par numéro d'OP.
 *
 * C'est une fonction d'AFFICHAGE : elle ne touche ni les temps, ni les
 * numéros, ni la base. Le serveur continue de relire `ORDER BY op_num`,
 * et c'est très bien — l'ordre de stockage n'a pas à porter une décision
 * de lecture.
 *
 * @returns {Array<{cle, label, aide, lignes: Array<{ligne, index}>}>}
 *   chaque ligne garde son INDEX d'origine : l'écran modifie la gamme
 *   par index, et un tri qui perdrait l'index ferait éditer la mauvaise
 *   ligne — silencieusement.
 */
export function ordonnerEnBlocs(gamme = []) {
  const avecIndex = (gamme ?? []).map((ligne, index) => ({ ligne, index }));
  return BLOCS_GAMME.map((bloc) => ({
    ...bloc,
    lignes: avecIndex
      .filter(({ ligne }) => blocDeLaLigne(ligne) === bloc.cle)
      .sort((a, b) => {
        const d = num(a.ligne?.op_num, 0) - num(b.ligne?.op_num, 0);
        // À numéro égal, on garde l'ordre d'arrivée : deux lignes de
        // même OP dans un même bloc n'ont pas d'ordre naturel, et en
        // inventer un ferait bouger l'écran d'un rendu à l'autre.
        return d !== 0 ? d : a.index - b.index;
      }),
  }));
}

/** Les trois unités. Miroir du CHECK en base. */
export const UNITES_GAMME = ['par_piece', 'par_lot', 'par_projet'];

/**
 * Les unités autorisées PAR CATÉGORIE.
 *
 * Cette table est la forme lisible de la contrainte `CHECK` de
 * `piece_gamme`. Elle vit ici pour que le front puisse la faire
 * respecter à la saisie — mais **elle ne remplace pas la contrainte** :
 * une règle métier évidente qui ne vit que dans une fonction JavaScript
 * finit toujours par être contournée par une autre fonction JavaScript.
 */
export const UNITES_PAR_CATEGORIE = {
  // ── Vocabulaire courant ──
  // La première pièce ne se paie qu'une fois : c'est elle qui coûte.
  prototype: ['par_lot'],
  // La production, elle, se paie à chaque pièce. Le `par_lot` reste
  // ouvert pour le DÉBIT d'une barre entière : la scie tourne une fois
  // pour la série.
  production: ['par_piece', 'par_lot'],
  // Le contrôle porte les DEUX : on contrôle la première pièce à fond
  // (par lot), puis on contrôle la production (par pièce). C'est ce que
  // Charles décrit, et c'est pour ça que « Contrôle » est un total à
  // part et non une part du cycle.
  controle: ['par_lot', 'par_piece'],

  // ── Ancien vocabulaire, pour les devis déjà chiffrés ──
  cycle: ['par_piece', 'par_lot'],
  montage: ['par_lot'],
  programmation: ['par_projet'],
};

/** Le pas de numérotation : de 10 en 10, pour pouvoir intercaler. */
export const PAS_OP_NUM = 10;

const num = (v, d = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : d;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Une ligne de gamme est-elle valide ?
 *
 * Rend la liste des problèmes, vide si tout va bien. On ne jette pas :
 * une ligne fautive se SIGNALE et s'exclut du total, elle ne fait pas
 * échouer le calcul des autres.
 */
export function problemesLigne(ligne, index = 0) {
  const p = [];
  const ou = `OP ${ligne?.op_num ?? `#${index + 1}`}`;

  if (!CATEGORIES_GAMME.includes(ligne?.categorie)) {
    p.push(`${ou} : catégorie inconnue (${ligne?.categorie ?? 'absente'}).`);
    return p;
  }
  const autorisees = UNITES_PAR_CATEGORIE[ligne.categorie];
  if (!autorisees.includes(ligne.unite)) {
    p.push(
      `${ou} : une opération de ${ligne.categorie} se compte en `
      + `${autorisees.join(' ou ')}, pas en ${ligne.unite ?? 'unité absente'}.`,
    );
  }
  // Un temps NÉGATIF est une faute de saisie ; un temps à zéro est une
  // décision — une opération peut légitimement ne rien coûter (transfert
  // automatique quasi instantané). Zéro décidé, pas zéro converti.
  if (ligne.temps_min == null || !Number.isFinite(Number(ligne.temps_min))) {
    p.push(`${ou} : temps absent.`);
  } else if (Number(ligne.temps_min) < 0) {
    p.push(`${ou} : temps négatif (${ligne.temps_min} min).`);
  }
  return p;
}

/**
 * LA DÉRIVATION — les trois totaux, depuis la gamme.
 *
 * @param {Array} gamme  les lignes de `piece_gamme`
 * @param {object} [opts]
 * @param {boolean} [opts.programmationDejaFaite]  le programme existe
 *   déjà : révision, relance, commande répétée. La programmation vaut
 *   alors **zéro** — c'est la première chose que Charles verra si on se
 *   trompe.
 * @returns {{cycle_par_piece, cycle_par_lot, montage_lot,
 *            programmation_projet, lignes_ignorees, problemes}}
 */
export function deriverTemps(gamme = [], opts = {}) {
  const problemes = [];
  const lignesIgnorees = [];

  // Le vocabulaire COURANT.
  let piece1Lot = 0;
  let productionPiece = 0;
  let productionPieceSeule = 0;
  let productionLot = 0;
  let controleLot = 0;
  let controlePiece = 0;
  // L'ANCIEN, pour les gammes déjà chiffrées.
  let cycleParPiece = 0;
  let cycleParLot = 0;
  let montageLot = 0;
  let programmationProjet = 0;

  /**
   * ── LES OP QUI PORTENT UN PROTOTYPE — 26 août 2026 ──
   *
   * La règle du 12 août dit `production × (quantité − 1)`, parce que
   * **le prototype contient déjà la première pièce**. Elle n'est vraie
   * que si l'OP porte les DEUX lignes.
   *
   * Sur le Palier D001234 (devis 2026-08-070), aucune ne les portait :
   * la gamme alternait OP10 prototype, OP20 production, OP30 prototype…
   * Ce sont des paires logiques — « bridage en étau » va avec
   * « surfaçage face 1 » — mais numérotées à part.
   *
   * Résultat à quantité 1 : les huit lignes de production multipliées
   * par ZÉRO. **286 minutes, presque cinq heures de travail réel, jamais
   * facturées.** On facturait dix minutes pour scier un barreau de
   * 540 mm en C45 — le temps de la préparation, pas celui de la coupe.
   *
   * > **On ne retire une pièce que si elle est comptée ailleurs.**
   *
   * Sans ligne prototype sur la même OP, il n'y a pas de première pièce
   * déjà comptée : rien à soustraire, donc `× quantité`. La règle
   * retrouve sa raison d'être au lieu de s'appliquer par habitude.
   *
   * Le contrôle est en CODE et pas dans le prompt : une gamme mal
   * appariée — par le modèle ou saisie à la main — perdrait sinon du
   * temps en silence, et un prix trop bas ne se voit pas.
   */
  const opsAvecPrototype = new Set(
    (gamme ?? [])
      .filter((l) => l?.categorie === 'prototype')
      .map((l) => num(l.op_num, -1)),
  );

  (gamme ?? []).forEach((ligne, i) => {
    const p = problemesLigne(ligne, i);
    if (p.length) {
      // ── Une ligne fautive s'EXCLUT et se DIT ──
      //
      // L'inclure quand même reviendrait à additionner un temps dont on
      // ne sait pas s'il se multiplie ou non : le total serait faux et
      // d'apparence normale. L'ignorer en silence produirait un total
      // trop bas, tout aussi silencieux. On l'exclut ET on le nomme.
      problemes.push(...p);
      lignesIgnorees.push(ligne?.op_num ?? i);
      return;
    }

    const t = num(ligne.temps_min);
    switch (ligne.categorie) {
      case 'prototype':
        piece1Lot += t;
        break;
      case 'production':
        if (ligne.unite !== 'par_piece') productionLot += t;
        else if (opsAvecPrototype.has(num(ligne.op_num, -1))) productionPiece += t;
        // Production SEULE : son OP ne porte pas de prototype, donc sa
        // première pièce n'est comptée nulle part ailleurs.
        else productionPieceSeule += t;
        break;
      case 'controle':
        if (ligne.unite === 'par_piece') controlePiece += t;
        else controleLot += t;
        break;
      case 'cycle':
        if (ligne.unite === 'par_piece') cycleParPiece += t;
        else cycleParLot += t;
        break;
      case 'montage':
        montageLot += t;
        break;
      default:
        programmationProjet += t;
    }
  });

  // Le programme existe déjà : la programmation vaut zéro. C'est un zéro
  // DÉCIDÉ — quelqu'un a coché la case, ou le rapprochement l'a proposée
  // cochée — donc une valeur, pas une absence.
  const dejaFaite = opts.programmationDejaFaite === true;

  // ── LE PROGRAMME EXISTE DÉJÀ ──
  //
  // Avant, ça mettait la programmation à zéro : elle avait sa ligne à
  // elle. Elle est maintenant DANS « prototype », avec le montage — et le
  // montage, lui, se refait à chaque lot. On ne peut donc plus annuler
  // la ligne entière.
  //
  // On retire donc la part `programmation_incluse_min` que l'agent
  // déclare sur la ligne « prototype », et rien d'autre. Sans cette part,
  // on ne peut pas soustraire — et dans ce cas on ne soustrait RIEN
  // plutôt que de deviner un pourcentage.
  const progIncluse = (gamme ?? [])
    .filter((l) => l.categorie === 'prototype')
    .reduce((somme, l) => somme + num(l.programmation_incluse_min), 0);
  const progEvitee = dejaFaite ? Math.min(progIncluse, piece1Lot) : 0;

  return {
    // ── Le vocabulaire COURANT ──
    prototype_lot: round2(piece1Lot - progEvitee),
    /**
     * La production dont l'OP porte AUSSI un prototype : la première
     * pièce y est déjà comptée, donc `× (quantité − 1)`.
     */
    production_piece: round2(productionPiece),
    /**
     * La production dont l'OP n'a PAS de prototype : rien n'a compté sa
     * première pièce, donc `× quantité`. Séparée et non fondue : les
     * deux ne se multiplient pas pareil, et un total qui les mélange ne
     * peut plus se justifier ligne à ligne.
     */
    production_piece_seule: round2(productionPieceSeule),
    production_lot: round2(productionLot),
    controle_lot: round2(controleLot),
    controle_piece: round2(controlePiece),
    programmation_incluse: round2(progIncluse),

    // ── L'ANCIEN, pour les devis déjà chiffrés ──
    cycle_par_piece: round2(cycleParPiece),
    cycle_par_lot: round2(cycleParLot),
    montage_lot: round2(montageLot),
    programmation_projet: dejaFaite ? 0 : round2(programmationProjet),
    // Ce que la programmation AURAIT coûté si le programme n'existait
    // pas. Affiché barré à côté du zéro : sans lui, « 0 min » se lit
    // comme « il n'y a pas de programmation », alors que la vraie
    // information est « elle est déjà payée ».
    programmation_evitee: dejaFaite ? round2(programmationProjet + progEvitee) : 0,
    programmation_deja_faite: dejaFaite,
    lignes_ignorees: lignesIgnorees,
    problemes,
    // Un AVERTISSEMENT, et pas un problème : la ligne est bonne et son
    // temps compte. C'est une ligne qui MANQUE — la distinction décide
    // de ce que l'écran affiche et de ce que le total additionne.
    ops_sans_montage: opsSansMontage(gamme, opts.typesDeMachines ?? {}),
    debits_en_prototype: debitsEnPrototype(gamme, opts.typesDeMachines ?? {}),
    // Un débit sur un tour qui s'alimente en barre : le tronçonnage est
    // dans le cycle, la ligne fait double emploi. SIGNALÉ, jamais retiré.
    debits_sur_tour_a_barre: debitsSurTourABarre(
      gamme, opts.typesDeMachines ?? {}, opts.passagesDeBarre ?? {}, opts.diametreBrut ?? null,
    ),
  };
}

/**
 * Les trois temps de la PIÈCE, gamme et saisies manuelles confondues.
 *
 * ── §8.4 — deux niveaux de correction, et les deux comptent ──
 *
 * Le deviseur n'est **jamais obligé** de descendre dans la gamme. Il
 * corrige soit un des trois totaux — rapide, grossier, ce qu'il fera le
 * plus souvent — soit la gamme.
 *
 * Un total saisi à la main est **figé** : il porte « saisi à la main »,
 * et la gamme ne l'écrase pas. C'est l'invariant 3 pris à l'envers —
 * *une décision humaine ne s'efface pas toute seule.*
 *
 * Le CONFLIT n'est pas résolu ici : il est SIGNALÉ. « Vous aviez saisi
 * 65 min de montage. La nouvelle gamme donne 72 min. » C'est l'écran qui
 * demande, et personne d'autre qui tranche.
 *
 * @param {object} piece  porte `cycle_time_min`, `setup_time_min`,
 *   `programming_time_min` (les saisies) et `temps_saisis_a_la_main`
 *   (la liste des champs figés).
 * @param {Array} gamme
 */
export function tempsDeLaPiece(piece = {}, gamme = []) {
  const derive = deriverTemps(gamme, {
    programmationDejaFaite: piece.programmation_deja_faite === true,
  });
  const aUneGamme = (gamme ?? []).length > 0;
  /**
   * Quel MODÈLE la gamme emploie. Il se déduit des lignes, jamais d'un
   * drapeau : un drapeau en base serait une seconde source de vérité,
   * et c'est toujours celle qui ment.
   */
  const modeleOP = (gamme ?? []).some(
    (l) => ['prototype', 'production', 'controle'].includes(l.categorie),
  );
  const figes = new Set(piece.temps_saisis_a_la_main ?? []);

  /**
   * Un champ figé garde sa valeur saisie ; sinon la gamme commande.
   *
   * Sans gamme, on retombe sur la saisie : c'est le mode d'avant le
   * 5 août, et il reste valable tant que l'agent n'a pas produit de
   * gamme. Ce n'est pas un repli dégradé — c'est le deviseur qui chiffre
   * à la main, ce qu'il a toujours eu le droit de faire.
   */
  const estPresent = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

  const resoudre = (champ, valeurGamme, saisie, proposeIA) => {
    if (figes.has(champ)) {
      return { valeur: num(saisie), source: 'saisi', gamme: aUneGamme ? valeurGamme : null };
    }
    if (aUneGamme) return { valeur: valeurGamme, source: 'gamme', gamme: valeurGamme };
    if (estPresent(saisie)) {
      return { valeur: num(saisie), source: 'saisi_sans_gamme', gamme: null };
    }
    // ── LE TROISIÈME ÉCHELON : la proposition de l'agent ──
    //
    // « Refuser d'inventer une LECTURE n'est pas refuser de proposer une
    // ESTIMATION » (§2bis). Un temps n'est écrit sur aucun plan : c'est
    // du jugement, donc on PROPOSE toujours, avec sa provenance.
    //
    // Sans cet échelon, une pièce dont l'agent avait proposé
    // 36 / 45 / 90 min sortait « Prix indisponible — aucun temps
    // saisi » : le programme avait payé une estimation, l'affichait
    // juste au-dessus, et refusait de s'en servir.
    if (estPresent(proposeIA)) {
      return { valeur: num(proposeIA), source: 'estime', gamme: null };
    }
    return { valeur: 0, source: 'absent', gamme: null };
  };

  // ── LES DEUX VOCABULAIRES TOMBENT DANS LES MÊMES PANIERS ──
  //
  // Une gamme NEUVE n'a ni `cycle` ni `montage` ; une ANCIENNE n'a ni
  // `production` ni `prototype`. Additionner les deux est donc sans
  // risque, et c'est ce qui permet à un devis d'août de se rouvrir au
  // centime près pendant qu'un devis de septembre emploie le nouveau
  // modèle.
  //
  //   ce qui tourne, à chaque pièce   → taux MACHINE
  //   ce qui se prépare, une fois     → taux HUMAIN
  //
  // Le CONTRÔLE par pièce est à part : il se multiplie par la quantité
  // comme la production, mais c'est un opérateur qui mesure, pas une
  // machine. Les deux taux valent 80 €/h aujourd'hui — le prix est donc
  // le même — mais ils peuvent diverger, et surtout la distinction est
  // ce qui rendra le retour d'atelier comparable.
  // ── LE CONTRÔLE EST FONDU DANS SES DEUX VOISINS ──
  //
  // Décision de Charles, 11 août. Il l'était à l'AFFICHAGE seulement, et
  // ça produisait deux chiffres pour une même chose : le cadre Temps
  // annonçait 80 min de prototype pendant que la synthèse en facturait
  // 100. Un champ modifiable dont la valeur diffère de ce qu'on lit à
  // côté est pire qu'un champ absent.
  //
  // Il est donc fondu ICI, à la source. Conséquence assumée : le
  // contrôle de production passe au taux MACHINE avec la production qui
  // le porte. Les deux taux valent 80 €/h, donc aucun prix ne bouge — et
  // c'est le modèle de Charles : le contrôle de la série fait partie de
  // la production.
  const cycle = resoudre(
    'cycle',
    round2(derive.cycle_par_piece + derive.production_piece
      + derive.production_piece_seule + derive.controle_piece),
    piece.cycle_time_min, piece.cycle_time_min_ia,
  );
  const montage = resoudre(
    'montage',
    // Le PROTOTYPE seul : montage de la machine et programmation. Le
    // contrôle du premier article a son propre panier — l'y inclure
    // faisait afficher « Prototype 100 min » là où la gamme dit 80, et
    // « Contrôle 5 » là où elle en compte 25. Un total qui absorbe un
    // autre total les rend faux tous les deux.
    round2(derive.montage_lot + derive.prototype_lot + derive.controle_lot),
    piece.setup_time_min, piece.setup_time_min_ia,
  );
  const programmation = resoudre(
    'programmation', derive.programmation_projet, piece.programming_time_min,
    piece.programming_time_min_ia,
  );
  // Le CONTRÔLE de production passe par la même cascade que les deux
  // autres : gamme, sinon saisie, sinon proposition de l'agent. Il en
  // était exclu — donc corrigeable seulement en descendant dans la
  // gamme, alors que le §8.4 promet qu'on n'y est jamais obligé.


  // ── Le CONFLIT, signalé et jamais résolu en silence ──
  const conflits = [];
  for (const [champ, r, libelle] of [
    ['cycle', cycle, 'de cycle'],
    ['montage', montage, 'de montage'],
    ['programmation', programmation, 'de programmation'],
  ]) {
    if (r.source === 'saisi' && r.gamme != null && round2(r.gamme) !== round2(r.valeur)) {
      conflits.push({
        champ,
        saisi: round2(r.valeur),
        gamme: round2(r.gamme),
        message:
          `Vous aviez saisi ${round2(r.valeur)} min ${libelle} à la main. `
          + `La gamme donne ${round2(r.gamme)} min.`,
      });
    }
  }

  return {
    cycle_time_min: round2(cycle.valeur),
    setup_time_min: round2(montage.valeur),
    programming_time_min: round2(programmation.valeur),
    // Le cycle PAR LOT n'a pas de champ sur la pièce : il ne vient que
    // de la gamme (le débit d'une barre). Un total saisi à la main ne
    // peut donc pas le figer, et c'est cohérent — personne ne saisit
    // « du cycle par lot » à la main.
    cycle_par_lot: round2(derive.cycle_par_lot + derive.production_lot),

    /**
     * ── UN SIGNAL, PAS UNE ENTRÉE DE CALCUL ──
     *
     * La production dont l'OP ne porte PAS de ligne prototype. Sa
     * première pièce n'est donc comptée nulle part, et la règle
     * `× (quantité − 1)` la fait disparaître à quantité 1.
     *
     * **Le calcul ne la traite pas à part, et c'est voulu** (formule v7,
     * 26 août 2026) : la règle a été expressément demandée par le client
     * et elle vaut pour toute la production. Corriger le calcul pour
     * rattraper une gamme mal appariée l'aurait rendue inexplicable.
     *
     * Ce nombre sert donc à MESURER ce que la gamme perd — combien de
     * minutes s'évaporent faute d'appariement — et à le montrer. La
     * correction est en amont : faire produire des paires.
     *
     * Zéro dès que le total est figé à la main : on ne connaît alors pas
     * la répartition, et un total saisi est une décision du deviseur.
     */
    production_pleine_min: cycle.source === 'gamme' ? round2(derive.production_piece_seule) : 0,

    /**
     * ── LA PREMIÈRE PIÈCE EST DÉJÀ DANS LE PROTOTYPE ──
     *
     * Décision de Charles, 12 août 2026, sur « Banc SA2Fir » : une
     * pièce à faire, et le programme comptait un prototype PUIS une
     * production. « Quand le prototype est fini je n'ai plus de pièces
     * pour la production. »
     *
     * Il a raison, et le défaut n'est pas propre à la quantité 1 : le
     * prototype contient le montage, la programmation ET l'usinage du
     * premier article. Multiplier la production par la quantité facture
     * donc onze pièces d'usinage pour dix.
     *
     * La production se compte sur les pièces qui RESTENT à faire :
     *
     *     production × (quantité − 1)
     *
     * À une pièce, ça vaut zéro tout seul — sans cas particulier. Un
     * cas spécial à 1 aurait laissé l'erreur d'un cycle à 2, 10 et 50,
     * invisible, et fait passer le prix unitaire pour une exception
     * alors que c'est la même règle.
     *
     * ── CE QUI GARDE L'ANCIEN COMPTE, ET POURQUOI ──
     *
     * Une gamme de l'ancien modèle (`cycle` / `montage` /
     * `programmation`) comptait CHAQUE pièce : son montage ne contenait
     * pas le premier article. Elle garde donc `× quantité`, sinon un
     * devis déjà remis changerait de prix.
     *
     * Même règle pour une pièce SANS gamme, chiffrée à la main : rien
     * ne dit que le temps saisi contient le premier article, et on ne
     * retire jamais une pièce d'un chiffre qu'on n'a pas produit.
     */
    modele_op: aUneGamme && modeleOP,

    sources: {
      cycle: cycle.source,
      montage: montage.source,
      programmation: programmation.source,
    },
    conflits,
    derive,
  };
}

/**
 * Les totaux À AFFICHER, quel que soit le modèle de la gamme.
 *
 * Une gamme neuve rend Prototype / Production / Contrôle ; une gamme d'un
 * devis déjà chiffré rend Cycle / Montage / Programmation. Sans cette
 * fonction, chaque écran referait le même `if` — l'écran de chiffrage,
 * la feuille d'atelier, le retour d'atelier, le PDF — et le jour où
 * l'un des quatre serait oublié, il afficherait « 0 min » sur une pièce
 * parfaitement chiffrée. Un zéro d'affichage, sans auteur.
 *
 * @returns {Array<{cle:string,label:string,portee:string,minutes:number}>}
 *   uniquement les totaux NON NULS, dans l'ordre de lecture.
 */
export function totauxAffichables(derive = {}) {
  const nouveau = ['prototype_lot', 'production_piece', 'production_lot', 'controle_lot', 'controle_piece']
    .some((k) => num(derive[k]) > 0);

  // ── LE CONTRÔLE REJOINT SES DEUX VOISINS — 11 août 2026 ──
  //
  // Décision de Charles : le contrôle du premier article appartient au
  // PROTOTYPE, celui de la série à la PRODUCTION. C'est vrai du travail
  // — on ne mobilise pas un poste de contrôle, on contrôle ce qu'on
  // vient de faire — et ça ramène le devis à DEUX totaux lisibles.
  //
  // Il garde ses lignes propres dans la gamme et son étiquette sur la
  // fiche d'atelier : c'est là qu'il faut le voir, pour le corriger et
  // pour le chronométrer. Ce sont les TOTAUX qui le fondent.
  const lignes = nouveau
    ? [
      {
        cle: 'prototype',
        label: 'Mise en route',
        portee: 'pour le lot',
        minutes: num(derive.prototype_lot) + num(derive.controle_lot),
      },
      {
        cle: 'production',
        label: 'Production',
        portee: 'par pièce',
        minutes: num(derive.production_piece) + num(derive.controle_piece),
      },
      { cle: 'debit', label: 'Débit', portee: 'pour le lot', minutes: num(derive.production_lot) },
    ]
    : [
      { cle: 'cycle', label: 'Cycle', portee: 'par pièce', minutes: num(derive.cycle_par_piece) },
      { cle: 'debit', label: 'Débit', portee: 'pour le lot', minutes: num(derive.cycle_par_lot) },
      { cle: 'montage', label: 'Montage', portee: 'pour le lot', minutes: num(derive.montage_lot) },
      { cle: 'programmation', label: 'Programmation', portee: 'une seule fois', minutes: num(derive.programmation_projet) },
    ];

  return lignes.filter((l) => l.minutes > 0);
}

/**
 * LES OP QUI PRODUISENT SANS QU'ON AIT MONTÉ LA PIÈCE.
 *
 * ── Ce que ça vaut, et pourquoi c'est en CODE ──
 *
 * Le modèle est « une OP = une prise, et chaque OP porte DEUX lignes » :
 * un `prototype` par lot — montage, programmation, premier article — et
 * une `production` par pièce. Une OP qui n'a que sa production est une
 * pièce qu'on usine sans l'avoir montée.
 *
 * Le prompt de l'agent d'estimation le dit trois fois, dont une en
 * capitales. Le 14 août 2026, sur le devis 2026-08-046, il a quand même
 * rendu une « OP 10 — production » seule, et Charles a dû ajouter le
 * montage manquant à la main. **Une consigne de prompt est une
 * intention, pas une garantie** : la règle vit donc aussi ici.
 *
 * ── Ce qu'on NE fait PAS : le corriger ──
 *
 * Fabriquer la ligne manquante demanderait d'inventer son temps. Un
 * montage à 0 min serait un zéro CONVERTI — une absence déguisée en
 * valeur, qui rendrait le prix trop bas sans que rien ne le dise. Une
 * durée devinée serait pire encore, puisqu'elle aurait l'air décidée.
 *
 * On le SIGNALE, et le temps existant compte normalement : il manque une
 * ligne, il n'y a pas de ligne fautive.
 *
 * Ne vaut que pour le modèle COURANT. Une gamme de devis déjà chiffré
 * emploie `cycle`/`montage`/`programmation`, où le montage est global :
 * l'y appliquer signalerait toutes les anciennes gammes d'un coup.
 */
/**
 * LES MACHINES QUI NE PORTENT JAMAIS DE MONTAGE — 27 août 2026
 *
 * Décision de Charles : **on garde le débit sans montage, donc jamais
 * le débit en prototype.** Scier un barreau à longueur, c'est une butée
 * et un serrage ; ce n'est pas la mise en train d'une machine à
 * commande numérique, avec sa programmation et son premier article.
 *
 * La reconnaissance est DÉTERMINISTE et ne demande rien au modèle :
 * c'est le `type` de la machine du parc. Elle ne passe donc ni par le
 * libellé (que le modèle écrit) ni par `operation_id` — que l'agent
 * laisse à `null` sur les 164 lignes de gamme existantes.
 */
export const TYPES_SANS_MONTAGE = ['scie'];

/**
 * `{ id: type }` depuis le parc, pour les fonctions ci-dessous.
 *
 * `shared/` ne lit jamais la base : l'appelant fournit le parc qu'il a
 * déjà sous la main — l'écran comme le service d'estimation.
 */
export function typesDeMachines(machines = []) {
  const out = {};
  for (const m of machines ?? []) {
    if (m?.id != null) out[String(m.id)] = m.type ?? null;
  }
  return out;
}

/**
 * Le PASSAGE DE BARRE de chaque machine, en mm.
 *
 * Séparé de `typesDeMachines` pour ne pas changer la forme d'une carte
 * dont trois appelants dépendent. Une machine sans passage renseigné
 * n'entre PAS dans la carte : `undefined` veut dire « on ne sait pas »,
 * et ça ne se confond pas avec zéro.
 */
export function passagesDeBarre(machines = []) {
  const out = {};
  for (const m of machines ?? []) {
    const v = Number(m?.passage_barre_mm);
    if (m?.id != null && Number.isFinite(v) && v > 0) out[String(m.id)] = v;
  }
  return out;
}

// ══════════════════════════════════════════════════════════════
// UN TOUR ALIMENTÉ EN BARRE N'A PAS DE DÉBIT SÉPARÉ.
// ──────────────────────────────────────────────────────────────
// Il tronçonne la pièce à la fin de son cycle : c'est une passe, pas un
// sciage. Sur la « Visserie hexagonale M6 » (200 pièces sur CTX 400,
// barre hexa), l'agent a proposé une ligne de débit de 3 min/pièce là
// où Mertens compte ZÉRO — dix heures de sciage sur une pièce qui n'en
// demande aucune.
//
// ── POURQUOI C'EST ICI ET PLUS DANS LE PROMPT ──
//
// La règle a d'abord été écrite dans le prompt v14. Mesurée le
// 7 septembre en rejouant le banc SANS elle : **la ligne de débit est
// rigoureusement identique**, avec et sans. Une consigne de prompt est
// une intention, pas une garantie — c'est déjà écrit au §7, et c'est
// pour ça que `TYPES_SANS_MONTAGE` vit en code.
//
// ── POURQUOI C'EST UN SIGNAL ET PAS UNE SUPPRESSION ──
//
// Retirer la ligne changerait le prix sans que personne l'ait décidé.
// Le programme SIGNALE, l'homme tranche — comme `debits_en_prototype`
// et `ops_sans_montage` juste au-dessus.
//
// ── CE QU'IL FAUT POUR QU'IL PARLE ──
//
// `machines.passage_barre_mm`, renseigné sur UNE machine sur cinq au
// 7 septembre 2026. Une machine sans passage connu ne déclenche RIEN :
// on ne suppose pas qu'un tour avale la barre parce qu'on n'a pas
// rempli la fiche. C'est l'invariant 5 appliqué à une absence.
// ══════════════════════════════════════════════════════════════

/**
 * Les OP dont le DÉBIT tourne sur une machine qui s'alimente en barre.
 *
 * @param {Array} gamme
 * @param {Object} types      id → type de machine
 * @param {Object} passages   id → passage de barre en mm
 * @param {number|null} diametreBrut  Ø du brut, s'il est connu
 */
export function debitsSurTourABarre(gamme = [], types = {}, passages = {}, diametreBrut = null) {
  const d = num(diametreBrut, 0);
  const out = [];
  for (const l of gamme ?? []) {
    const cle = String(l?.machine_id ?? '');
    const passage = passages?.[cle];
    if (!(passage > 0)) continue;
    // Une ligne de débit se reconnaît à sa machine : une scie n'est pas
    // concernée — c'est bien elle qui débite. Ici on parle d'un TOUR qui
    // porterait une ligne de débit alors qu'il avale sa barre.
    if (types?.[cle] === 'scie') continue;
    if (!/d[ée]bit|sciage|tron[çc]onn/i.test(`${l?.libelle ?? ''} ${l?.detail ?? ''}`)) continue;
    // Le brut doit PASSER. Un Ø trop gros se débite pour de vrai, et la
    // ligne est légitime. Ø inconnu : on ne tranche pas.
    if (d > 0 && d > passage) continue;
    out.push(num(l.op_num, -1));
  }
  return [...new Set(out)];
}

/** Cette ligne tourne-t-elle sur une machine qui ne se monte pas ? */
function surMachineSansMontage(ligne, types) {
  const t = types?.[String(ligne?.machine_id)] ?? null;
  return Boolean(t) && TYPES_SANS_MONTAGE.includes(t);
}

/**
 * Les lignes de DÉBIT rangées en prototype — la faute inverse.
 *
 * Une ligne de scie en `prototype` facture une mise en train sur un
 * poste qui n'en a pas. Elle est rare (2 lignes en base au 27 août) et
 * elle ne se voit pas : le temps est plausible, l'unité est valide, et
 * le total monte.
 */
export function debitsEnPrototype(gamme = [], types = {}) {
  return (gamme ?? [])
    .filter((l) => l?.categorie === 'prototype' && surMachineSansMontage(l, types))
    .map((l) => num(l.op_num, -1));
}

export function opsSansMontage(gamme = [], types = {}) {
  const lignes = gamme ?? [];
  const modeleOP = lignes.some((l) => CATEGORIES_GAMME_COURANTES.includes(l?.categorie));
  if (!modeleOP) return [];

  const prototypes = new Set(
    lignes.filter((l) => l?.categorie === 'prototype').map((l) => num(l.op_num, -1)),
  );
  const vus = new Set();
  const manquants = [];
  for (const l of lignes) {
    if (l?.categorie !== 'production') continue;
    const op = num(l.op_num, -1);
    if (prototypes.has(op) || vus.has(op)) continue;
    // ── L'EXCEPTION DU DÉBIT ──
    //
    // Une OP dont TOUTE la production tourne sur une machine sans
    // montage n'a pas à porter de prototype. « Toute » et non « au
    // moins une » : une OP qui scie ET fraise se monte pour fraiser, et
    // l'exception ne doit pas la faire passer entre les gouttes.
    const production = lignes.filter(
      (x) => x?.categorie === 'production' && num(x.op_num, -1) === op,
    );
    if (production.every((x) => surMachineSansMontage(x, types))) continue;
    vus.add(op);
    manquants.push(op);
  }
  return manquants;
}

/**
 * Renumérote une gamme de 10 en 10, dans l'ordre où elle arrive.
 *
 * À n'appeler que sur demande EXPLICITE. Renuméroter à chaque
 * enregistrement détruirait la raison d'être du pas de 10 : pouvoir
 * intercaler une OP 25 entre 20 et 30 et l'y retrouver.
 */
export function renumeroter(gamme = []) {
  // ── ON RENUMÉROTE LES OP, PAS LES LIGNES — 27 août 2026 ──
  //
  // Cette fonction faisait `map((l, i) => op_num = (i+1) * 10)` : un
  // numéro par LIGNE. Or une OP en porte DEUX depuis le 11 août — le
  // prototype et la production. Un clic sur « Renuméroter » DOUBLAIT
  // donc le nombre d'OP, en séparant chaque paire :
  //
  //   avant   10:proto  10:produ  20:proto  20:produ   → 2 OP
  //   après   10:proto  20:produ  30:proto  40:produ   → 4 OP
  //
  // La gamme restait juste au centime — les temps et les unités ne
  // bougent pas — mais la pièce était décrite comme montée quatre fois
  // au lieu de deux, et chaque réestimation reprenait la forme cassée,
  // l'agent étant fidèle à la gamme sur laquelle il s'ancre. C'est
  // l'origine de la gamme à 18 OP du devis 2026-08-070.
  //
  // On regroupe donc par `op_num` EXISTANT, dans l'ordre d'apparition,
  // et chaque groupe reçoit un numéro. Une OP reste une OP.
  const lignes = gamme ?? [];
  // ── UNE LIGNE SANS NUMÉRO EST SEULE ──
  //
  // On ne regroupe que ce qui partage un numéro RÉEL. Deux lignes sans
  // `op_num` ne sont pas une paire : ce sont deux inconnues, et les
  // fondre en une OP inventerait un appairage que personne n'a décidé.
  // Chacune reçoit donc sa propre clé.
  const cleDe = (l, i) => (Number.isFinite(Number(l?.op_num)) ? `n${Number(l.op_num)}` : `seule${i}`);
  const ordre = [];
  lignes.forEach((l, i) => {
    const cle = cleDe(l, i);
    if (!ordre.includes(cle)) ordre.push(cle);
  });
  const neuf = new Map(ordre.map((cle, i) => [cle, (i + 1) * PAS_OP_NUM]));
  return lignes.map((l, i) => ({ ...l, op_num: neuf.get(cleDe(l, i)) }));
}

/**
 * Monte ou descend une ligne d'une place dans la gamme.
 *
 * ── POURQUOI CE N'EST PAS QU'UN ÉCHANGE DE POSITIONS ──
 *
 * L'ordre affiché n'est pas celui du tableau : le serveur relit la gamme
 * `ORDER BY op_num`. Permuter deux lignes sans échanger leurs numéros
 * donnerait donc un déplacement qui TIENT à l'écran et disparaît au
 * rechargement — le pire des deux, puisque rien ne le dirait.
 *
 * On échange donc les deux, la place ET le numéro.
 *
 * Le cas limite qui rendrait ça faux : deux lignes qui portent le même
 * `op_num` — l'échange ne les sépare pas, et l'ordre reste indécidable.
 * Là, et là seulement, on renumérote toute la gamme de 10 en 10. C'est
 * un repli, il est franc, et il produit exactement ce que le deviseur
 * vient de demander.
 *
 * Rien d'autre ne bouge : ni les temps, ni les catégories, ni les
 * libellés — un numéro d'OP écrit DANS un libellé (« OP 20 — prototype »)
 * appartient au deviseur, pas au programme.
 *
 * @param {Array} gamme
 * @param {number} index ligne à déplacer
 * @param {-1|1} sens -1 pour monter, +1 pour descendre
 */
export function deplacerLigne(gamme = [], index, sens) {
  // ── ON DÉPLACE UNE OP, PAS UNE LIGNE — 27 août 2026 ──
  //
  // Cette fonction échangeait deux LIGNES voisines. Sur le modèle en OP,
  // où une opération en porte deux, ça produisait deux résultats faux et
  // aucun des deux ne se voyait :
  //
  //  · déplacer la production de l'OP 10 sous le prototype de l'OP 20
  //    donnait « OP 10 : deux prototypes » et « OP 20 : deux
  //    productions » — une pièce montée deux fois puis usinée deux fois ;
  //  · déplacer une ligne À L'INTÉRIEUR d'une paire tombait sur le repli
  //    `memeNumero → renumeroter(lignes)`, qui SÉPARAIT toutes les
  //    paires de la gamme. Or « deux lignes de même numéro » n'est pas
  //    un cas limite depuis le 11 août : c'est le cas NORMAL.
  //
  // Le geste du deviseur n'a jamais été « je monte cette demi-ligne » :
  // c'est « je fais cette opération avant celle-là ». On déplace donc
  // l'OP entière, avec ses deux lignes, et l'ordre à l'intérieur d'une
  // OP n'a pas de sens à changer — le prototype précède la production
  // par nature.
  const lignes = [...(gamme ?? [])];
  if (index < 0 || index >= lignes.length) return lignes;

  // Les OP, dans l'ordre d'apparition.
  const ordre = [];
  for (const l of lignes) {
    const cle = num(l?.op_num, 0);
    if (!ordre.includes(cle)) ordre.push(cle);
  }
  const opCourante = num(lignes[index]?.op_num, 0);
  const rang = ordre.indexOf(opCourante);
  const rangCible = rang + sens;
  // Hors des bornes : on rend la gamme telle quelle. Les boutons sont
  // déjà désactivés aux extrémités ; ceci est la ceinture.
  if (rang < 0 || rangCible < 0 || rangCible >= ordre.length) return lignes;

  const opCible = ordre[rangCible];

  // Les deux OP échangent leur NUMÉRO — c'est lui qui porte l'ordre,
  // puisque le serveur relit la gamme `ORDER BY op_num`. Permuter les
  // positions sans les numéros donnerait un déplacement qui tient à
  // l'écran et disparaît au rechargement : le pire des deux, puisque
  // rien ne le dirait.
  //
  // Rien d'autre ne bouge : ni les temps, ni les catégories, ni les
  // libellés — un numéro d'OP écrit DANS un libellé (« OP 20 —
  // prototype ») appartient au deviseur, pas au programme.
  const renumerotee = lignes.map((l) => {
    const n = num(l?.op_num, 0);
    if (n === opCourante) return { ...l, op_num: opCible, source: 'corrige' };
    if (n === opCible) return { ...l, op_num: opCourante, source: 'corrige' };
    return l;
  });

  // Puis on remet la LISTE dans l'ordre des numéros, en gardant l'ordre
  // interne de chaque OP : le prototype avant la production.
  const parOp = new Map();
  for (const l of renumerotee) {
    const n = num(l?.op_num, 0);
    if (!parOp.has(n)) parOp.set(n, []);
    parOp.get(n).push(l);
  }
  return [...parOp.keys()].sort((x, y) => x - y).flatMap((n) => parOp.get(n));
}


/**
 * Le multiplicateur affiché en bout de ligne, dans le récapitulatif.
 *
 * `× 10 / × 1 / × 1` à quantité 10, et `× 50 / × 1 / × 1` à cinquante :
 * les deux dernières ne bougent pas d'une minute. C'est la démonstration
 * la plus convaincante de l'outil, et elle tient dans trois nombres.
 */
export function multiplicateurs(qty = 1, modeleOP = false) {
  const q = Math.max(1, Math.round(num(qty, 1)));
  // En modèle OP, la production ne porte que sur les pièces qui RESTENT :
  // la première est usinée dans le prototype.
  return { cycle: modeleOP ? Math.max(0, q - 1) : q, montage: 1, programmation: 1 };
}

/**
 * Les lignes de gamme qu'une FEUILLE D'ATELIER présente — papier et
 * téléphone. Une seule fonction pour les deux supports.
 *
 * Retour de Philippe, 20/10/2026 (devis 2026-10-024 et 026, quantité 1) :
 * le papier disait « une seule pièce : pas de production », et la page
 * du QR proposait quand même les lignes de production à remplir. Deux
 * supports, deux règles, et la seconde n'était écrite nulle part.
 *
 * La règle est celle du PRIX : on retire une ligne seulement quand le
 * calcul la multiplie par zéro. En modèle OP, à une pièce, c'est le cas
 * de toute ligne `par_piece` de production ET de contrôle — elles se
 * comptent sur `quantité − 1` (`tempsDeLaPiece` fond le contrôle de série
 * dans le cycle). Le DÉBIT (`production` `par_lot`) et le contrôle du
 * premier article restent : ils sont facturés à une pièce, donc ils se
 * font. L'ancien filtre du papier retirait aussi le débit — une scie qui
 * tourne vraiment, et que la feuille taisait.
 *
 * @returns {{lignes: Array, masquees: number}} `masquees` : combien de
 *          lignes sont retirées, pour que la feuille le DISE.
 */
export function lignesDeLaFeuille(gamme = [], qty = 1) {
  const toutes = gamme ?? [];
  const modeleOP = toutes.some((l) => CATEGORIES_GAMME_COURANTES.includes(l?.categorie));
  const unePiece = Math.max(1, Math.round(num(qty, 1))) === 1;
  const aZero = (l) => modeleOP && unePiece && l?.unite === 'par_piece'
    && (l?.categorie === 'production' || l?.categorie === 'controle');
  const lignes = toutes.filter((l) => !aZero(l));
  return { lignes, masquees: toutes.length - lignes.length };
}

/**
 * Ce qui CHANGE entre deux gammes — §8.6, et c'est du CODE, pas du modèle.
 *
 * ── Pourquoi le diff n'est pas demandé à l'agent ──
 *
 * Il pourrait le rendre, et il se tromperait sans qu'on le sache : un
 * modèle à qui l'on demande « qu'as-tu changé ? » raconte ce qu'il
 * croit avoir changé. Ici la question a une réponse exacte — deux
 * listes, une comparaison — et tout ce qui est calculable se calcule.
 *
 * C'est aussi ce qui rend la promesse tenable : **on ne remplace jamais
 * une gamme en silence.** L'écran montre ligne par ligne ce qui entre,
 * ce qui sort et ce qui bouge, et l'humain applique. Un remplacement
 * muet ferait exactement ce que le §8.6 interdit.
 *
 * ── L'APPARIEMENT : LE LIBELLÉ D'ABORD, LE NUMÉRO ENSUITE ──
 *
 * L'ordre inverse paraissait naturel — le prompt demande à l'agent de
 * garder le numéro d'une opération qu'il ne touche pas — et le premier
 * appel réel l'a démoli.
 *
 * Sur une révision qui SUPPRIMAIT une opération, l'agent a renuméroté
 * toute la suite. Apparier par numéro a donc mis face à face « OP 60
 * Montage prise 3 » et « OP 60 Perçage-taraudage prise 2 » : quatre
 * lignes annoncées « modifiées » alors qu'aucune ne l'était, avec des
 * changements absurdes — « catégorie montage → cycle », « libellé
 * Montage prise 3 → Perçage-taraudage prise 2 ». L'écran sur lequel on
 * décide d'appliquer racontait n'importe quoi.
 *
 * **Le libellé est l'IDENTITÉ d'une opération ; le numéro n'est que sa
 * position.** Deux libellés identiques à deux numéros différents sont
 * la même opération déplacée ; deux libellés différents au même numéro
 * sont deux opérations différentes. On apparie donc sur ce qui
 * identifie, et le numéro ne sert que de recours quand le libellé a
 * changé.
 *
 * La prise départage les homonymes : « Montage prise 1 » et « Montage
 * prise 2 » portent des libellés distincts ici, mais rien ne garantit
 * qu'ils le resteront.
 *
 * > **Un diff ne doit pas dépendre de l'obéissance du modèle.** Le
 * > prompt lui demande de conserver ses numéros ; le premier appel réel
 * > montre qu'il ne le fait pas toujours, et l'affichage doit rester
 * > juste quand il ne le fait pas.
 *
 * @returns {{lignes: Array, resume: object}} une entrée par opération,
 *          dans l'ordre de la gamme d'ARRIVÉE, les retirées à leur place
 *          d'origine.
 */
/**
 * Le libellé ramené à ce qui IDENTIFIE l'opération.
 *
 * ── Pourquoi « prise 3 » en est retiré ──
 *
 * L'agent écrit le numéro de prise DANS le libellé : « Perçage-taraudage
 * prise 3 ». Quand une révision supprime un montage, l'opération devient
 * « Perçage-taraudage prise 2 » — même opération, même outil, même
 * travail, sur une prise qui a changé de numéro.
 *
 * Sans cette normalisation, les deux libellés sont deux chaînes
 * différentes : l'appariement échoue, l'écran annonce une suppression
 * plus un ajout, et le vrai changement — « elle passe en prise 2 » —
 * n'apparaît nulle part. Mesuré sur le premier appel réel de révision,
 * le 10 août.
 *
 * Le numéro de prise n'est pas perdu : il est comparé à part, et le
 * diff le rapporte comme un changement de `prise_num`. C'est sa place —
 * une donnée, pas un morceau de nom.
 */
const clefLibelle = (op) =>
  String(op?.libelle ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // « prise 3 », « prise3 », « en prise 2 » : la position, pas
    // l'identité.
    .replace(/\bprises?\s*\d+/g, '')
    .replace(/[^a-z0-9]/g, '');

/** Les champs dont un changement se VOIT et se raconte. */
const CHAMPS_SUIVIS = [
  ['temps_min', 'temps'],
  ['machine_id', 'machine'],
  ['categorie', 'catégorie'],
  ['unite', 'unité'],
  // `prise_num` reste suivie : une opération DÉPLACÉE d'une prise à
  // l'autre est un vrai changement, et c'est même le cœur d'une
  // consigne du type « pas de reprise ». Ce qui a changé, c'est qu'un
  // champ ABSENT ne compte plus comme modifié — voir plus bas.
  ['prise_num', 'prise'],
  ['libelle', 'libellé'],
];

export function diffGamme(avant = [], apres = []) {
  const restants = [...(avant ?? [])];
  const prendre = (op) => {
    const clef = clefLibelle(op);
    let i = -1;
    // 1. Le libellé ET la prise : l'identité la plus complète.
    if (clef) {
      // Le libellé ET la prise. `clefLibelle` retire « prise N » du
      // libellé : sans ce départage, « Fraisage prise 1 » et « Fraisage
      // prise 2 » deviennent la même clef et s'apparient à la même
      // ligne. Mesuré sur un couple RÉEL : 8 lignes au lieu de 6.
      //
      // Il sert à APPARIER, pas à signaler : `prise_num` a quitté
      // `CHAMPS_SUIVIS` parce qu'il se déduit côté serveur, mais il
      // reste une part de l'identité d'une ligne.
      i = restants.findIndex(
        (x) => clefLibelle(x) === clef && num(x.prise_num) === num(op.prise_num),
      );
      // 2. Le libellé seul : l'opération a changé de prise, elle reste
      //    la même opération.
      if (i < 0) i = restants.findIndex((x) => clefLibelle(x) === clef);
    }
    // 3. Le numéro, en dernier recours : le libellé a été réécrit. Ne
    //    l'accepter QUE si le candidat n'a pas déjà un homonyme ailleurs
    //    dans la gamme d'arrivée — sinon on rapatrie par le numéro une
    //    opération que son vrai jumeau va réclamer juste après.
    if (i < 0 && op.op_num != null) {
      i = restants.findIndex(
        (x) => x.op_num === op.op_num
          && !(apres ?? []).some((y) => y !== op && clefLibelle(y) === clefLibelle(x)),
      );
    }
    return i < 0 ? null : restants.splice(i, 1)[0];
  };

  const lignes = [];
  for (const op of apres ?? []) {
    const ancienne = prendre(op);
    if (!ancienne) {
      lignes.push({ statut: 'ajoutee', avant: null, apres: op, changements: [] });
      continue;
    }
    const changements = [];
    for (const [champ, libelle] of CHAMPS_SUIVIS) {
      // ── UN CHAMP ABSENT N'EST PAS UN CHAMP MODIFIÉ ──
      //
      // Le modèle ne rend plus `prise_num` : elle se déduit côté serveur
      // depuis le 11 août. Comparé au `prise_num` lu en base, ça
      // annonçait « prise 1 → null » sur CHAQUE ligne d'une révision —
      // du bruit qui décrit un champ que personne n'a touché, et qui
      // noie les vrais changements dans le panneau.
      //
      // `undefined` veut dire « pas fourni », `null` veut dire « vidé ».
      // Les confondre, c'est inventer une décision.
      if (op[champ] === undefined) continue;
      const a = ancienne[champ] ?? null;
      const b = op[champ] ?? null;
      const egal = champ === 'temps_min' || champ === 'prise_num' || champ === 'machine_id'
        ? num(a) === num(b)
        : String(a ?? '') === String(b ?? '');
      if (!egal) changements.push({ champ, libelle, avant: a, apres: b });
    }
    lignes.push({
      statut: changements.length ? 'modifiee' : 'inchangee',
      avant: ancienne,
      apres: op,
      changements,
    });
  }

  // Ce qui n'a pas été apparié a été RETIRÉ. Il garde sa place d'origine
  // dans l'affichage : une opération qui disparaît doit se voir là où
  // elle était, pas reléguée en fin de liste.
  for (const op of restants) {
    const i = (avant ?? []).indexOf(op);
    lignes.splice(Math.min(i, lignes.length), 0, {
      statut: 'retiree', avant: op, apres: null, changements: [],
    });
  }

  const compter = (s) => lignes.filter((l) => l.statut === s).length;
  const tempsAvant = deriverTemps(avant ?? []);
  const tempsApres = deriverTemps(apres ?? []);
  return {
    lignes,
    resume: {
      ajoutees: compter('ajoutee'),
      retirees: compter('retiree'),
      modifiees: compter('modifiee'),
      inchangees: compter('inchangee'),
      // Ce que la révision change AU TOTAL. C'est le seul chiffre qui
      // dit si elle vaut la peine d'être appliquée.
      // ── L'ÉCART SE MESURE SUR LES TOTAUX AFFICHÉS ──
      //
      // Il se mesurait sur `cycle_par_piece`, `montage_lot` et
      // `programmation_projet` — l'ANCIEN vocabulaire, donc zéro sur
      // toute gamme d'aujourd'hui. Le panneau annonçait « cycle +0 ·
      // montage +0 · programmation +0 » pendant que le prototype passait
      // de 90 à 60 minutes.
      //
      // Charles lisait « ça ne change rien » sur une révision qui
      // changeait tout, et concluait que l'agent ne l'écoutait pas.
      // Un résumé qui contredit son propre détail est pire qu'un résumé
      // absent.
      prototype: round2(
        minutesParBloc(tempsApres).mise_en_route - minutesParBloc(tempsAvant).mise_en_route,
      ),
      production: round2(
        minutesParBloc(tempsApres).production - minutesParBloc(tempsAvant).production,
      ),
      par_lot: round2(
        (tempsApres.production_lot + tempsApres.cycle_par_lot)
        - (tempsAvant.production_lot + tempsAvant.cycle_par_lot),
      ),
    },
  };
}

/**
 * Les minutes des deux BLOCS, depuis une dérivation (`deriverTemps`).
 *
 * Mise en route = prototype + contrôle du premier article (+ montage et
 * programmation de l'ancien vocabulaire) ; production = production à la
 * pièce + contrôle de série (+ cycle ancien). Le débit par lot n'entre
 * dans aucun des deux : c'est un temps de LOT, et l'additionner à des
 * minutes par pièce mélangerait deux unités.
 */
export function minutesParBloc(derive = {}) {
  return {
    mise_en_route: round2(num(derive.prototype_lot) + num(derive.controle_lot)
      + num(derive.montage_lot) + num(derive.programmation_projet)),
    // `production_piece_seule` : la production d'une OP SANS mise en
    // route (un débit à la scie). C'est une minute par pièce comme les
    // autres ; le résumé de `diffGamme` l'oubliait avant cette fonction.
    production: round2(num(derive.production_piece) + num(derive.production_piece_seule)
      + num(derive.controle_piece) + num(derive.cycle_par_piece)),
  };
}

/**
 * L'ÉCART proposé → retenu d'une correction de gamme, PAR BLOC.
 *
 * C'est la mesure que l'écran Apprentissage appelle « écart médian ».
 * Jusqu'au 28 septembre 2026 elle se calculait sur `delta_pct`, qui ne
 * portait que des champs de LECTURE — une quantité (1 → 207 : 20 600 %)
 * et un identifiant de matière (10 → 12 : « 20 % ») — et jamais un temps.
 *
 * Signé : +150 veut dire que Mertens retient 2,5 fois ce que l'agent
 * proposait. `null` pour un bloc que l'agent n'avait pas chiffré : on ne
 * divise pas par zéro, et on ne compte pas un ajout comme un écart.
 */
export function ecartDeGamme(proposee = [], retenue = []) {
  const p = minutesParBloc(deriverTemps(proposee ?? []));
  const r = minutesParBloc(deriverTemps(retenue ?? []));
  const pct = (a, b) => (a > 0 ? Math.round(((b - a) / a) * 1000) / 10 : null);
  return {
    mise_en_route: { propose: p.mise_en_route, retenu: r.mise_en_route, pct: pct(p.mise_en_route, r.mise_en_route) },
    production: { propose: p.production, retenu: r.production, pct: pct(p.production, r.production) },
  };
}

// ══════════════════════════════════════════════════════════════
// LA CHARGE EN HEURES ET EN JOURS — réunion du 1er septembre 2026.
// ──────────────────────────────────────────────────────────────
// Raph : « je veux pouvoir dire *on en a pour une semaine* sans
// recalculer. » L'écran ne parlait qu'en minutes et en euros — deux
// unités qui ne répondent pas à la question de l'atelier, qui est une
// question de PLANNING.
//
// Le calcul n'a rien de nouveau : ce sont exactement les minutes que le
// prix facture. Elles vivent ici, en un seul endroit, parce que le banc
// de gammes les vérifie aussi — deux implémentations divergeraient, et
// c'est toujours celle qu'on ne teste pas qui ment.
// ══════════════════════════════════════════════════════════════

/**
 * Une journée d'atelier.
 *
 * C'est une CONVENTION d'affichage, pas une donnée de production : on
 * convertit des minutes en jours pour situer un ordre de grandeur, pas
 * pour promettre une date. L'écran l'écrit en clair (« sur une base de
 * 8 h ») plutôt que de laisser croire à un délai calculé.
 */
export const HEURES_PAR_JOUR = 8;

/**
 * LES MINUTES RÉELLEMENT FACTURÉES pour le lot entier.
 *
 * `work` est le bloc `detail.work` rendu par `computePiece`. On ne
 * refait pas la somme depuis la gamme : le multiplicateur de production
 * (`qté − 1` en modèle OP) y est déjà résolu, et le recalculer ici
 * ouvrirait la porte à deux règles d'amortissement au lieu d'une.
 */
export function minutesDuLot(work = {}) {
  return round2(
    num(work.cycle_time_min) * num(work.production_multiplicateur, 1)
    + num(work.cycle_par_lot_min)
    + num(work.setup_time_min)
    + num(work.programming_time_min),
  );
}

/**
 * La charge en heures et en jours, pour l'annoncer à l'atelier.
 *
 * Les jours sont arrondis au dixième : « 0,2 jour » et « 5 jours » sont
 * les deux exemples donnés en réunion, et un dixième de journée est la
 * plus petite unité qui veuille dire quelque chose à l'ordonnancement.
 */
export function chargeAtelier(minutes) {
  const m = num(minutes);
  return {
    minutes: round2(m),
    heures: Math.round((m / 60) * 10) / 10,
    jours: Math.round((m / 60 / HEURES_PAR_JOUR) * 10) / 10,
  };
}
